/**
 * CredasIntegrationSection — Settings → Integrations card for the
 * Credas ID-verification / right-to-work API.
 *
 * Lets a tenant admin:
 *   1. Toggle the integration on/off
 *   2. Pick environment (Sandbox / Production)
 *   3. Paste their per-tenant API key (stored encrypted server-side)
 *   4. Test the connection (calls Credas /api/v2/ci/journeys)
 *   5. Pick a default Journey from a dropdown populated live from Credas
 *   6. (Advanced) override the magic-link Swagger path + webhook URL
 *
 * The API key is never read back to the browser — only a masked
 * `…1234` preview is shown.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CredasIntegrationSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    environment: 'demo',
    api_key_set: false,
    api_key_preview: null,
    journey_id: '',
    actor_id: 110,
    magic_link_path: '',
    webhook_url: '',
    base_url: '',
    updated_at: null,
  });
  // Local-only state for inputs that don't round-trip
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // ── Load saved config on mount ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/credas/config`);
        if (!alive) return;
        setConfig((prev) => ({ ...prev, ...data }));
      } catch (e) {
        if (alive) toast.error('Could not load Credas settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Auto-load journeys when a key is configured + enabled ──
  useEffect(() => {
    if (!config.api_key_set || !config.enabled) return;
    let alive = true;
    setLoadingJourneys(true);
    axios
      .get(`${API_URL}/credas/journeys`)
      .then(({ data }) => { if (alive) setJourneys(data?.journeys || []); })
      .catch(() => { if (alive) setJourneys([]); })
      .finally(() => { if (alive) setLoadingJourneys(false); });
    return () => { alive = false; };
  }, [config.api_key_set, config.enabled, config.environment]);

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/credas/config`, patch);
      setConfig((prev) => ({ ...prev, ...data }));
      toast.success('Credas settings saved');
      // Clear the in-memory key draft after a save that included it
      if ('api_key' in patch) setApiKeyDraft('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKeyDraft.trim()) {
      toast.error('Paste your Credas API key first');
      return;
    }
    await saveField({ api_key: apiKeyDraft.trim() });
  };

  const handleClearKey = async () => {
    if (!window.confirm('Remove the saved Credas API key for this workspace?')) return;
    await saveField({ api_key: '' });
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const { data } = await axios.post(`${API_URL}/credas/test-connection`);
      if (data?.ok) {
        setConnectionStatus({ ok: true, message: `Connected — ${data.journey_count} journey(s) visible.` });
        toast.success(`Credas connection OK (${data.journey_count} journeys)`);
      } else {
        setConnectionStatus({ ok: false, message: data?.message || 'Connection failed', details: data?.details });
        toast.error(`Credas: ${data?.message || 'Connection failed'}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setConnectionStatus({ ok: false, message: msg });
      toast.error(`Credas: ${msg}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="neumorphic" data-testid="credas-integration-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-muted-foreground">
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Loading Credas settings…
          </div>
        </CardContent>
      </Card>
    );
  }

  const connected = config.api_key_set && config.enabled;

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="credas-integration-card">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Credas ID Verification</h2>
          <p className="text-sm text-muted-foreground">
            Automate right-to-work and ID checks. We create the Credas process via API,
            suppress their email, and embed the candidate link in your own onboarding emails as <code className="text-xs bg-muted px-1 rounded">{'{{credasLink}}'}</code>.
          </p>
        </div>
        {connected ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full" data-testid="credas-status-connected">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Active</span>
          </div>
        ) : config.api_key_set ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Key saved · disabled</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
            <XCircle className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Not configured</span>
          </div>
        )}
      </div>

      {/* Enable toggle */}
      <div className="flex items-center justify-between py-3 border-b border-slate-200">
        <div>
          <Label className="text-sm font-medium">Enable Credas for this workspace</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            When OFF, the &quot;Run Credas check&quot; checkbox and &quot;Send to Credas&quot; button are hidden everywhere.
          </p>
        </div>
        <Switch
          checked={!!config.enabled}
          onCheckedChange={(v) => saveField({ enabled: v })}
          disabled={saving}
          data-testid="credas-enable-switch"
        />
      </div>

      {/* Environment */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">Environment</Label>
        <div className="mt-2 flex gap-2">
          {[
            { key: 'demo', label: 'Sandbox', hint: 'portal.credasdemo.com' },
            { key: 'production', label: 'Production', hint: 'portal.credas.com' },
          ].map((env) => {
            const active = config.environment === env.key;
            return (
              <button
                key={env.key}
                type="button"
                disabled={saving}
                onClick={() => saveField({ environment: env.key })}
                className={`flex-1 px-3 py-2 rounded-lg border text-left transition-colors ${
                  active
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`credas-env-${env.key}`}
              >
                <div className="text-sm font-medium">{env.label}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{env.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* API key */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">API Key</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Generate from the Credas portal → Admin Portal → your user → Issue API Key.
          Stored encrypted (AES) in our database; never returned in plain text.
        </p>
        {config.api_key_set ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono text-slate-600">
              {config.api_key_preview || '••••••••'}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearKey}
              disabled={saving}
              data-testid="credas-clear-key-btn"
            >
              Remove
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder="Paste your Credas API key…"
                value={apiKeyDraft}
                onChange={(e) => setApiKeyDraft(e.target.value)}
                className="pr-9 font-mono text-sm"
                data-testid="credas-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              size="sm"
              onClick={handleSaveKey}
              disabled={saving || !apiKeyDraft.trim()}
              data-testid="credas-save-key-btn"
            >
              Save key
            </Button>
          </div>
        )}
        {config.api_key_set && (
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !config.enabled}
              data-testid="credas-test-btn"
            >
              {testing ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Testing…</>
              ) : (
                'Test connection'
              )}
            </Button>
            {connectionStatus && (
              <span className={`text-xs ${connectionStatus.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                {connectionStatus.ok ? '✓ ' : '✗ '}{connectionStatus.message}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Journey */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">Default Journey</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          The Credas journey (e.g. &quot;Right to Work — standard&quot;) used when a recruiter
          ticks &quot;Run Credas check&quot; without picking a specific one.
        </p>
        {loadingJourneys ? (
          <div className="text-xs text-muted-foreground flex items-center">
            <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
            Fetching journeys from Credas…
          </div>
        ) : journeys.length > 0 ? (
          <select
            className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            value={config.journey_id || ''}
            onChange={(e) => saveField({ journey_id: e.target.value })}
            disabled={saving}
            data-testid="credas-journey-select"
          >
            <option value="">— Select a journey —</option>
            {journeys.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}{j.description ? ` — ${j.description}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Paste journey GUID directly…"
              value={config.journey_id || ''}
              onChange={(e) => setConfig((p) => ({ ...p, journey_id: e.target.value }))}
              onBlur={(e) => saveField({ journey_id: e.target.value })}
              className="font-mono text-sm"
              data-testid="credas-journey-input"
            />
            <p className="text-[11px] text-muted-foreground">
              {config.api_key_set
                ? "Couldn't auto-fetch journeys — Credas returned an empty list. Paste the GUID manually or check the Sandbox/Production toggle above."
                : "Save your API key first to auto-populate this list."}
            </p>
          </div>
        )}
      </div>

      {/* Advanced */}
      <div className="pt-3">
        <button
          type="button"
          onClick={() => setShowAdvanced((s) => !s)}
          className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="credas-advanced-toggle"
        >
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Advanced settings
        </button>
        {showAdvanced && (
          <div className="mt-3 space-y-4">
            <div>
              <Label className="text-xs font-medium">Applicant Actor ID</Label>
              <Input
                type="number"
                value={config.actor_id ?? 110}
                onChange={(e) => setConfig((p) => ({ ...p, actor_id: Number(e.target.value) }))}
                onBlur={(e) => saveField({ actor_id: Number(e.target.value) })}
                className="font-mono text-sm mt-1"
                data-testid="credas-actor-id-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Credas role ID for the candidate. Default 110 (applicant) — see your Credas portal if you use a custom actor catalog.
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">Magic-link Swagger path</Label>
              <Input
                type="text"
                placeholder="/api/v2/ci/process/{process_id}/magic-link"
                value={config.magic_link_path || ''}
                onChange={(e) => setConfig((p) => ({ ...p, magic_link_path: e.target.value }))}
                onBlur={(e) => saveField({ magic_link_path: e.target.value })}
                className="font-mono text-xs mt-1"
                data-testid="credas-magic-link-path-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Override only if your Credas tenant&apos;s Swagger uses a non-standard path
                (look up KB <code className="bg-muted px-1 rounded">44002514711</code>).
                Supports <code className="bg-muted px-1 rounded">{'{process_id}'}</code> and <code className="bg-muted px-1 rounded">{'{entity_id}'}</code> placeholders.
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">Webhook URL (status updates)</Label>
              <Input
                type="text"
                placeholder={`${window.location.origin.replace('preview', 'hyredocs').replace('emergentagent.com', 'com')}/api/credas/webhook/<tenant_id>`}
                value={config.webhook_url || ''}
                onChange={(e) => setConfig((p) => ({ ...p, webhook_url: e.target.value }))}
                onBlur={(e) => saveField({ webhook_url: e.target.value })}
                className="font-mono text-xs mt-1"
                data-testid="credas-webhook-url-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Credas POSTs here when a candidate completes their journey. Leave blank to fall back to per-process webhooks (none).
              </p>
            </div>
            {config.base_url && (
              <div className="text-xs text-muted-foreground">
                Active base URL: <code className="font-mono bg-muted px-1 rounded">{config.base_url}</code>
                {config.updated_at && (
                  <>· last saved {new Date(config.updated_at).toLocaleString()}</>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
